import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// Componente de Loading
const LoadingCentralizado = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

// Formatação de telefone
const formatPhone = (value: string): string => {
  value = value.replace(/\D/g, '');
  if (value.length > 2) {
    value = `(${value.substring(0, 2)}) ${value.substring(
      2,
      7,
    )}-${value.substring(7, 11)}`;
  }
  return value;
};

// Limpeza de telefone
const cleanPhone = (value: string): string => {
  return '55' + value.replace(/\D/g, '');
};

// Componente da Primeira Etapa
const PrimeiraEtapaComponent = ({
  telefoneFormatado,
  handleAvançarClick,
  loading,
}: {
  telefoneFormatado: string;
  handleAvançarClick: () => void;
  loading: boolean;
}) => (
  <>
    <Typography variant="body1" sx={{ mb: 3 }}>
      Enviaremos um código por SMS para validar sua identidade. Clique no botão
      abaixo para enviá-lo ao número cadastrado em sua proposta. Se precisar
      alterar o número, entre em contato com nosso atendimento.
    </Typography>

    <TextField
      fullWidth
      margin="normal"
      label="Telefone Cadastrado"
      variant="outlined"
      value={telefoneFormatado}
      disabled
    />

    <Divider sx={{ my: 2 }} />

    <Button
      variant="contained"
      color="primary"
      fullWidth
      sx={{ mt: 2 }}
      onClick={handleAvançarClick}
      disabled={loading}
    >
      {loading ? <CircularProgress size={24} /> : 'Enviar Código'}
    </Button>
  </>
);

// Componente da Segunda Etapa
const SegundaEtapaComponent = ({
  telefoneFormatado,
  codigo,
  handleCodigoChange,
  handleSubmitCodigo,
  handleVoltarClick,
  loading,
}: {
  telefoneFormatado: string;
  codigo: string;
  handleCodigoChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmitCodigo: () => void;
  handleVoltarClick: () => void;
  loading: boolean;
}) => (
  <>
    <Typography variant="body1" gutterBottom>
      Insira o código enviado para: {telefoneFormatado}
    </Typography>

    <TextField
      fullWidth
      margin="normal"
      label="Código de 6 dígitos"
      variant="outlined"
      value={codigo}
      onChange={handleCodigoChange}
      required
      inputProps={{ maxLength: 6 }}
    />

    <Divider sx={{ my: 2 }} />

    <Box display="flex" gap={2}>
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleVoltarClick}
        disabled={loading}
      >
        Voltar
      </Button>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleSubmitCodigo}
        disabled={codigo.length !== 6 || loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Validar Código'}
      </Button>
    </Box>
  </>
);

// Componente Principal
const Proposta: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [pdfBase64, setPdfBase64] = useState('');
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState(false);
  const [proposalData, setProposalData] = useState<any>(null);
  const navigate = useNavigate();

  // Estados de autenticação
  const [telefoneFormatado, setTelefoneFormatado] = useState('');
  const [codigo, setCodigo] = useState('');
  const [step, setStep] = useState(1);
  const [authLoading, setAuthLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ texto: '', tipo: '' });

  const origem = location.pathname.includes('S4e')
    ? 3
    : location.pathname.includes('Empresa')
    ? 2
    : 1;

  // Carrega dados da proposta
  useEffect(() => {
    const fetchData = async () => {
      try {
        const urlBase = process.env.REACT_APP_OG_API_URL;
        const endpoint =
          origem === 1
            ? `${urlBase}/generate/proposta?cd_associado=${id}`
            : origem === 3
            ? `${urlBase}/generate/propostaS4e?cpf=${id}`
            : `${urlBase}/generate/propostaEmpresa?cd_empresa=${id}`;

        const response = await axios.get(endpoint);
        setProposalData(response.data);
        setPdfBase64(response.data.proposta);
        setTelefoneFormatado(formatPhone(response.data.contato.telefone));
      } catch (error) {
        console.error('Erro ao buscar proposta:', error);
        setMensagem({ texto: 'Erro ao carregar proposta', tipo: 'error' });
        alert('erro');
        //navigate('/');
      }
    };

    fetchData();
  }, [id, origem, navigate]);

  // Handlers
  const handleAvançarClick = async () => {
    setAuthLoading(true);
    try {
      const response = await axios.post(
        'https://odontoauth.odontogroup.com.br/auth/register',
        { phone: cleanPhone(telefoneFormatado) },
      );

      if (response.status === 200 || response.status === 201) {
        setStep(2);
        setMensagem({
          texto: `Código enviado para ${telefoneFormatado}`,
          tipo: 'success',
        });
      }
    } catch (err) {
      setMensagem({
        texto: 'Erro ao enviar código. Tente novamente.',
        tipo: 'error',
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmitCodigo = async () => {
    setAuthLoading(true);
    try {
      const response = await axios.post(
        'https://odontoauth.odontogroup.com.br/auth/verify',
        {
          phone: cleanPhone(telefoneFormatado),
          code: codigo,
        },
      );

      if (response.data.status === 'success') {
        setAuthenticated(true);
        setPdfBase64(proposalData.proposta);
      } else {
        setMensagem({
          texto: response.data.error || 'Código inválido',
          tipo: 'error',
        });
      }
    } catch (err) {
      setMensagem({
        texto: 'Erro na verificação. Tente novamente.',
        tipo: 'error',
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVoltarClick = () => {
    setStep(1);
    setCodigo('');
  };

  const handleCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  const handleCloseMensagem = () => {
    setMensagem({ texto: '', tipo: '' });
  };

  // Renderização condicional
  if (!proposalData) {
    return <LoadingCentralizado />;
  }

  if (!authenticated) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: '600px' }}>
            <Typography variant="h5" gutterBottom align="center">
              {step === 1
                ? 'Confirmação de Identidade'
                : 'Verificação de Código'}
            </Typography>

            {step === 1 ? (
              <PrimeiraEtapaComponent
                telefoneFormatado={telefoneFormatado}
                handleAvançarClick={handleAvançarClick}
                loading={authLoading}
              />
            ) : (
              <SegundaEtapaComponent
                telefoneFormatado={telefoneFormatado}
                codigo={codigo}
                handleCodigoChange={handleCodigoChange}
                handleSubmitCodigo={handleSubmitCodigo}
                handleVoltarClick={handleVoltarClick}
                loading={authLoading}
              />
            )}
          </Paper>
        </Box>

        <Snackbar
          open={!!mensagem.texto}
          autoHideDuration={6000}
          onClose={handleCloseMensagem}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseMensagem}
            severity={mensagem.tipo === 'success' ? 'success' : 'error'}
          >
            {mensagem.texto}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  return (
    <Container>
      <embed
        src={`data:application/pdf;base64,${pdfBase64}`}
        type="application/pdf"
        width="100%"
        height="900px"
        style={{ marginTop: '100px', border: '1px solid #ccc' }}
      />
    </Container>
  );
};

export default Proposta;
